/* =====================================================================
   Alex & Lace — STORY TEXT-TRAVEL (ROADMAP §911–916, "Our Story" 8pm.it
   mechanic). GSAP + ScrollTrigger. Sibling to al-hero-motion.js /
   al-ambient-motion.js — three focused modules, one motion voice.

   MECHANIC (re-enabled 2026-07-17, motion build #3; RETIMED 2026-07-18 — dead-space
   fix): DELIBERATE — meant to be noticed (unlike the ambient drift). Inside the tall
   (190vh) al-story section, both columns are `position: sticky` (native CSS — NO GSAP
   pin, NO position:fixed; the section has no overflow-clipping ancestor, verified), so
   the image stays full-height in view while the section scrolls. The TEXT then TRAVELS
   UP through that pinned viewport — a scrubbed y translate on [data-al-story-text], 0.9
   scrub, riding Lenis on the --al-ease family.

   RETIME (2026-07-18) — the original v1 rested the text at the section's very END, so
   the reader stared at a blank left column through most of the transit and the settle
   landed after attention had moved on (same failure mode as the footer dock). Two
   changes fix it: (1) ARRIVE EARLY — the rise begins pre-pin (START_AT, section top
   still 70% down the viewport), so the heading is already ~half-risen and visible the
   moment the image pins (no blank column). (2) REST EARLY — a timeline runs the rise
   over the FIRST REST_AT of the scrubbed range and then HOLDS at rest, tuned so the
   text settles at ~60% of the PINNED transit and dwells rested (fully readable) for the
   remaining ~40% before release. The settle is now SEEN while reading, then rests.

   ROOT FIX for the old "tall empty scroll void": the 190vh sticky container was
   the defect only WITHOUT the motion. So the `.al-story--travel` class (which
   turns on the 190vh runway + sticky columns) is added HERE, coupled to the
   tween — never in the static markup. gsap.matchMedia gates both to desktop ≥1024
   + no-reduced-motion and auto-reverts (killing the tween) when the query stops
   matching; the returned cleanup removes the class. So JS-off / reduced-motion /
   mobile keep the COLLAPSED static layout (content height, text at rest, no
   void) — per the static-flush precedent — and only desktop-no-RM gets the
   tall runway + travel. The tall container and the motion are inseparable.

   Rest position: the text rests at y:0 = its CSS rest (the text column's sticky is
   TOP-anchored — flex-start + padding-top — as of 2026-08-02, so the rest sits high in the
   sticky viewport and is IDENTICAL on both placements regardless of how tall their copy is;
   it was flex-end + padding-bottom before that). GSAP animates FROM +TRAVEL·vh (below rest) UP to 0,
   so the rested (final) position is the transform-free state — JS-off / reduced-motion
   render it statically without motion (the hold-at-rest tail leaves y:0 unchanged).
   ===================================================================== */

(() => {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  const section = document.querySelector('[data-al-story]');
  const text = section && section.querySelector('[data-al-story-text]');
  if (!section || !text) return;

  gsap.registerPlugin(ScrollTrigger);

  // TRAVEL — fraction of viewport the text rises from below its rest → deliberate but calm.
  // SHARED CONSTANT (restored 2026-08-02, same day, superseding the brief per-instance experiment):
  // this is a motion-tuning number, not merchant content, so it lives here alongside REST_AT /
  // START_AT / SCRUB and the 190vh runway — one value, every placement. The per-instance
  // `data-al-story-travel` / "Text travel distance" setting that briefly existed is REMOVED: a
  // schema setting is the only thing that ever let the homepage and About placements drift apart,
  // and a merchant has no basis to judge 42 vs 50. No setting = they cannot diverge again.
  const TRAVEL = 0.42;
  const SCRUB = 0.9;
  // RETIMED 2026-07-18 (dead-space fix, same precedent as the footer dock): the rise must be SEEN.
  //   ARRIVE EARLY — begin the rise while the section is still entering (top at START_AT down the
  //     viewport), so by the time the sticky image is prominently pinned the text is already
  //     visibly rising, never a fully-blank column.
  //   REST EARLY — the text reaches its rest by REST_AT of the scrubbed range and HOLDS there for the
  //     remainder, so the settle happens WHILE the section is being read, then rests naturally to exit
  //     (instead of settling at 100%, after attention has moved on).
  const START_AT = '70%'; // section top this far down the viewport when the rise begins (pre-pin, so
                          //   the text is already ~half-risen and visible by the time the image pins)
  const REST_AT = 0.78;   // fraction of the scrubbed range at which the text reaches rest, then holds —
                          //   tuned so rest lands at ~62% of the PINNED transit (rise seen, calm tail)
  const vh = () => window.innerHeight;
  const mm = gsap.matchMedia();

  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    // Turn on the tall runway + sticky columns ONLY alongside the motion.
    section.classList.add('al-story--travel');

    // Timeline so the rise occupies only the FIRST REST_AT of the range and then holds at rest —
    // scrubbed, so scroll position drives it. Range: section entering (top at START_AT) → section
    // bottom reaches viewport bottom (sticky release). Text is at rest (y:0) well before release.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top ' + START_AT, // section top reaches START_AT down the viewport → rise begins (pre-pin)
        end: 'bottom bottom',     // section bottom reaches viewport bottom → sticky releases
        scrub: SCRUB,
        invalidateOnRefresh: true,
      },
    });
    tl.fromTo(
      text,
      { y: () => TRAVEL * vh() },
      { y: 0, ease: 'none', duration: REST_AT }
    ).to(text, { y: 0, duration: 1 - REST_AT }); // hold at rest through the remaining scroll

    // Cleanup when the query stops matching (resize to mobile / reduced-motion on): drop the runway
    // so the layout collapses back to its static resting state — no leftover 190vh void.
    return () => {
      tl.scrollTrigger && tl.scrollTrigger.kill();
      tl.kill();
      section.classList.remove('al-story--travel');
    };
  });

  // Late-layout refresh (CLAUDE guardrail): the Nohemi webfont swap + Lenis's async height:auto both
  // shift layout AFTER the trigger is built; recompute bounds on fonts.ready / 'load' / al:lenis-ready.
  const refreshST = () => ScrollTrigger.refresh();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshST);
  window.addEventListener('load', refreshST);
  window.addEventListener('al:lenis-ready', refreshST);
})();
